import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, IndianRupee, History, CheckCircle2, Loader2 } from 'lucide-react';

export default function ProcessPaymentModal({ isOpen, onClose, record, onConfirm }) {
  const [formData, setFormData] = useState({
    traveling_allowance: '',
    bonus: '',
    payment_method: 'Bank Transfer',
    payment_reference: '',
    payment_notes: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        traveling_allowance: '',
        bonus: '',
        payment_method: 'Bank Transfer',
        payment_reference: '',
        payment_notes: ''
      });
    }
  }, [isOpen]);

  if (!isOpen || !record) return null;

  const monthEarned = record.earned_salary_raw || 0;
  const traveling = parseFloat(formData.traveling_allowance) || 0;
  const bonus = parseFloat(formData.bonus) || 0;
  const totalNetPayable = monthEarned + traveling + bonus;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onConfirm({
        ...formData,
        net_salary: totalNetPayable,
        status: 'Paid'
      });
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Define the modal JSX
  const modalContent = (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-xl animate-in fade-in duration-300 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-[400px] my-auto overflow-hidden animate-in zoom-in-95 duration-200">
        
        {/* Header - Super Compact */}
        <div className="bg-[#f0fff9] px-6 py-3 flex items-center justify-between border-b border-[#e6f7ef]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#dcfaec] rounded-full flex items-center justify-center">
               <IndianRupee className="text-[#34b38a]" size={16} />
            </div>
            <h2 className="text-base font-bold text-[#1e293b]">Process Payment</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors p-1"><X size={18} /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          
          {/* Allowances Row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold text-[#64748b] uppercase tracking-wider mb-1">Traveling (₹)</label>
              <input 
                type="number"
                value={formData.traveling_allowance}
                onChange={(e) => setFormData({ ...formData, traveling_allowance: e.target.value })}
                className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg focus:border-[#34b38a] outline-none text-sm font-medium"
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-[#64748b] uppercase tracking-wider mb-1">Bonus (₹)</label>
              <input 
                type="number"
                value={formData.bonus}
                onChange={(e) => setFormData({ ...formData, bonus: e.target.value })}
                className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg focus:border-[#34b38a] outline-none text-sm font-medium"
                placeholder="0"
              />
            </div>
          </div>

          {/* Method Selection */}
          <div className="grid grid-cols-2 gap-3 items-end">
            <div>
              <label className="block text-[10px] font-bold text-[#64748b] uppercase tracking-wider mb-1">Method</label>
              <div className="relative">
                <select 
                  value={formData.payment_method}
                  onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                  className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg appearance-none focus:border-[#34b38a] outline-none text-sm font-medium cursor-pointer"
                >
                  <option value="Bank Transfer">Bank</option>
                  <option value="UPI">UPI</option>
                  <option value="Cash">Cash</option>
                </select>
                <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-bold text-[#64748b] uppercase tracking-wider mb-1">Ref ID</label>
              <input 
                type="text"
                value={formData.payment_reference}
                onChange={(e) => setFormData({ ...formData, payment_reference: e.target.value })}
                className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg focus:border-[#34b38a] outline-none text-sm font-medium"
                placeholder="TXN..."
              />
            </div>
          </div>

          <div>
             <label className="block text-[10px] font-bold text-[#64748b] uppercase tracking-wider mb-1">Payment Notes</label>
             <input 
               type="text"
               value={formData.payment_notes}
               onChange={(e) => setFormData({ ...formData, payment_notes: e.target.value })}
               className="w-full px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg focus:border-[#34b38a] outline-none text-sm font-medium"
               placeholder="Optional notes..."
             />
          </div>

          {/* Summary Table - Super Compact */}
          <div className="bg-[#f8fafc] rounded-lg border border-[#e2e8f0] text-sm overflow-hidden text-slate-700">
             <div className="px-4 py-1.5 flex justify-between items-center border-b border-[#e2e8f0]/50 text-[11px] font-bold text-[#64748b]">
                <span>Attendance Based: ₹{monthEarned.toLocaleString()}</span>
               <span>{traveling > 0 ? `+ Traveling: ₹${traveling}` : ''}</span>
            </div>
            <div className="px-4 py-2 flex justify-between items-center font-black">
               <span className="text-slate-800">TOTAL PAYABLE:</span>
               <span className="text-[#34b38a] text-lg">₹{totalNetPayable.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
          </div>

          {/* Bottom Actions */}
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-white border border-gray-200 rounded-lg text-[#64748b] font-bold text-sm hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-[#82d1b5] hover:bg-[#6fc4a5] text-white rounded-lg font-bold text-sm shadow-sm flex items-center justify-center gap-2"
            >
               {loading ? <Loader2 className="animate-spin" size={18} /> : "Confirm Payment"}
            </button>
          </div>
        </form>

      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
